import type { ResourceAbilityTuple } from '@gozoki/api-types'
import { Button, Tooltip, type ButtonProps } from '@mui/material'
import {
  createLink,
  useRouter,
  type AnyRoute,
  type LinkComponent,
  type LinkProps,
  type StaticDataRouteOption,
} from '@tanstack/react-router'
import React, { createElement, type ReactNode } from 'react'
import RbacAccessDeniedPage from '~/components/communication/RbacAccessDeniedPage'
import { useAbility, type AppAbility } from '~/utils/ability'
import type { FileRoutesByTo } from '../routeTree.gen'

export type RouteTo = Extract<keyof FileRoutesByTo, string>

export function useRouteStaticData(to: RouteTo): StaticDataRouteOption | undefined {
  const router = useRouter()
  type FileRouteData = FileRoutesByTo[keyof FileRoutesByTo]
  // @ts-expect-error
  const routeData = (router.routesByPath[to] ?? router.routesByPath[`${to}/`]) as FileRouteData
  return routeData.options.staticData
}

type MUILinkProps = Omit<LinkProps, 'children'> & {
  children: ReactNode
  authorized: boolean
} & Pick<ButtonProps, 'variant' | 'sx' | 'color'>

const PERMISSION_DENIED_MESSAGE = "Vous n'êtes pas autorisé à effectuer cette action"
type MuiLinkComponentWithHiddenAuthorized = React.ForwardRefExoticComponent<
  Omit<MUILinkProps, 'authorized'> & React.RefAttributes<HTMLAnchorElement>
>
const MUILinkComponent = React.forwardRef<HTMLAnchorElement, MUILinkProps>((props, ref) => {
  const isDisabled = props.disabled || !props.authorized

  if (!props.authorized) {
    return (
      <Tooltip title={PERMISSION_DENIED_MESSAGE}>
        <span>
          <Button ref={ref} component="a" {...props} disabled />
        </span>
      </Tooltip>
    )
  }
  return (
    <Button ref={ref} component="a" {...props} disabled={isDisabled}>
      {props.children as ReactNode}
    </Button>
  )
})

const CreatedLinkComponent = createLink(MUILinkComponent)

/** Disclaimer: only works with absolute urls */
export const AuthorizedButtonLink: LinkComponent<MuiLinkComponentWithHiddenAuthorized> = (
  props,
) => {
  const staticData = useRouteStaticData(props.to as RouteTo)
  const ability = useAbility()

  const permission = staticData?.permission ?? null
  const authorized = !permission || ability.can(...permission)
  const newProps = { ...props, authorized }

  return <CreatedLinkComponent {...newProps} />
}

class PermissionRouteError extends Error {
  constructor(permission: ResourceAbilityTuple) {
    super(`Missing permission: ${permission.join('.')}`)
    this.name = 'PermissionCheckError'
  }
}

export function withRoutePermissions<T extends AnyRoute>(route: T): T {
  const opts = route.options
  const _beforeLoad = opts.beforeLoad
  opts.beforeLoad = function beforeLoad(this: any, ctx: any) {
    if (this.staticData?.permission) {
      const ability: AppAbility = ctx.context.ability
      if (ability.cannot(...this.staticData.permission)) {
        throw new PermissionRouteError(this.staticData.permission)
      }
    }
    return _beforeLoad?.call(this, ctx)
  }
  const _errorComponent = opts.errorComponent
  opts.errorComponent = function errorComponent(props: any) {
    if (_errorComponent) {
      return createElement(_errorComponent, props)
    }
    if (props.error instanceof PermissionRouteError) {
      return <RbacAccessDeniedPage />
    }
    throw props.error
  }
  return route
}
